import {
  resolveExperienceProfile,
  resolveProductCapabilityStates,
  shouldRenderSurface,
} from '@tov-plus/capabilities';
import {
  msg,
  type AppNavigationSection,
  withAppNavigationSectionDefaults,
} from '@tov-plus/ui';

const guestSearchLabel = msg('general.fields.search.label');
const guestSearchSummary = msg(
  'app.navigation.guest_visible_event_summaries_for_the_current_occasion',
);

export function buildGuestNavigation(
  availableTargetsOrMultiLanguageEnabled: string[] | boolean = [
    'home',
    'today',
    'events',
    'messages',
    'my_invite',
    'recent_invites',
    'locations',
    'rsvp',
    'forms',
    'registry',
    'schedule',
    'seating',
    'gallery',
    'follow_up',
    'shared_planning',
    'access',
  ],
  attentionTarget: string | null = null,
  recommendedTarget = 'home',
  multiLanguageEnabled = false,
): AppNavigationSection[] {
  const availableTargets = Array.isArray(availableTargetsOrMultiLanguageEnabled)
    ? availableTargetsOrMultiLanguageEnabled
    : [
        'home',
        'today',
        'events',
        'messages',
        'my_invite',
        'recent_invites',
        'locations',
        'rsvp',
        'forms',
        'registry',
        'schedule',
        'seating',
        'gallery',
        'follow_up',
        'shared_planning',
        'access',
      ];
  const resolvedMultiLanguageEnabled = Array.isArray(
    availableTargetsOrMultiLanguageEnabled,
  )
    ? multiLanguageEnabled
    : availableTargetsOrMultiLanguageEnabled;
  const profile = resolveExperienceProfile({
    audience: 'guest',
    capabilityStates: resolveProductCapabilityStates({
      grants: resolvedMultiLanguageEnabled
        ? [
            {
              capabilityKey: 'multi_language',
              source: 'billing_entitlement',
              enabled: true,
              sourceKey: 'feature.multi_language',
              scopeType: 'occasion',
              scopeId: null,
            },
          ]
        : [],
    }),
  });

  const availableTargetSet = new Set(availableTargets);

  function includeTarget(target: string) {
    return (
      target === 'home' ||
      target === 'access' ||
      target === 'my_invite' ||
      target === 'follow_up' ||
      availableTargetSet.has(target)
    );
  }

  function withStatus(
    target: string,
    item: {
      id: string;
      label: string;
      href: string;
      summary: string;
      icon?: AppNavigationSection['items'][number]['icon'];
    },
  ) {
    const badges = [
      target === attentionTarget ? msg('app.navigation.needs_attention') : null,
      target === recommendedTarget ? msg('general.actions.next') : null,
    ].filter(Boolean);

    return {
      ...item,
      summary:
        badges.length > 0
          ? `${item.summary} (${badges.join(', ')})`
          : item.summary,
    };
  }

  return [
    {
      id: 'occasion',
      label: msg('general.fields.occasion.label'),
      items: [
        withStatus('home', {
          id: 'home',
          label: msg('app.navigation.overview'),
          href: '/',
          icon: 'house',
          summary: msg(
            'app.navigation.occasion_first_entry_for_guests_and_their_visible_events',
          ),
        }),
        availableTargetSet.has('today') ||
        availableTargetSet.has('schedule') ||
        availableTargetSet.has('events')
          ? withStatus('today', {
              id: 'today',
              label: msg('general.calendar.today'),
              href: '/today',
              icon: 'calendar-blank',
              summary: msg(
                'app.navigation.one_event_day_command_view_for_arrivals_live_changes_and_todays_schedule',
              ),
            })
          : null,
        includeTarget('events')
          ? withStatus('events', {
              id: 'events',
              label: msg('general.modules.events.name'),
              href: '/events',
              icon: 'calendar-blank',
              summary: msg(
                'app.navigation.guest_visible_event_summaries_for_the_current_occasion',
              ),
            })
          : null,
        includeTarget('locations')
          ? withStatus('locations', {
              id: 'locations',
              label: msg('general.modules.locations.name'),
              href: '/locations',
              icon: 'globe',
              summary: msg(
                'app.navigation.see_invitation_visible_venues_parking_accommodations_and_meeting_points',
              ),
            })
          : null,
      ].filter(Boolean) as AppNavigationSection['items'],
    },
    {
      id: 'guest-tools',
      label: msg('app.navigation.guest_tools'),
      items: [
        includeTarget('rsvp')
          ? withStatus('rsvp', {
              id: 'rsvp',
              label: msg('general.modules.rsvp.name'),
              href: '/rsvp',
              icon: 'checks',
              summary: msg(
                'app.navigation.confirm_invited_events_and_answer_rsvp_linked_questions',
              ),
            })
          : null,
        includeTarget('forms')
          ? withStatus('forms', {
              id: 'forms',
              label: msg('general.modules.forms.name'),
              href: '/forms',
              icon: 'file-text',
              summary: msg(
                'app.navigation.complete_guest_safe_forms_in_the_same_occasion_journey',
              ),
            })
          : null,
        includeTarget('registry')
          ? withStatus('registry', {
              id: 'registry',
              label: msg('general.modules.registry.name'),
              href: '/registry',
              icon: 'gift',
              summary: msg(
                'app.navigation.review_gift_items_filter_availability_and_claim_what_you_plan_to_bring',
              ),
            })
          : null,
        includeTarget('messages')
          ? withStatus('messages', {
              id: 'messages',
              label: msg('general.modules.messages.name'),
              href: '/messages',
              icon: 'envelope-simple',
              summary: msg(
                'app.navigation.review_planner_updates_delivery_history_and_guest_device_preferences',
              ),
            })
          : null,
        includeTarget('events') || includeTarget('locations')
          ? withStatus('search', {
              id: 'search',
              label: guestSearchLabel,
              href: '/search',
              icon: 'magnifying-glass',
              summary: guestSearchSummary,
            })
          : null,
        includeTarget('my_invite')
          ? withStatus('my_invite', {
              id: 'my-invite',
              label: msg('app.navigation.my_invite'),
              href: '/my-invite',
              icon: 'user',
              summary: msg(
                'app.navigation.review_invite_scope_rsvp_state_travel_packet_access_and_support_history_in',
              ),
            })
          : null,
        includeTarget('recent_invites')
          ? withStatus('recent_invites', {
              id: 'recent-invites',
              label: msg('app.navigation.recent_invites'),
              href: '/recent-invites',
              icon: 'books',
              summary: msg(
                'app.navigation.see_recently_opened_occasions_on_this_device_and_know_which_invite_to',
              ),
            })
          : null,
        includeTarget('gallery')
          ? withStatus('gallery', {
              id: 'gallery',
              label: msg('general.modules.gallery.name'),
              href: '/gallery',
              icon: 'images',
              summary: msg(
                'app.navigation.approved_memories_and_future_guest_upload_paths',
              ),
            })
          : null,
        includeTarget('follow_up')
          ? withStatus('follow_up', {
              id: 'follow-up',
              label: msg('app.navigation.follow_up'),
              href: '/follow-up',
              icon: 'arrow-right',
              summary: msg(
                'app.navigation.return_for_thank_yous_shared_memories_and_post_event_updates',
              ),
            })
          : null,
        includeTarget('schedule')
          ? withStatus('schedule', {
              id: 'schedule',
              label: msg('general.modules.schedule.name'),
              href: '/schedule',
              icon: 'scroll',
              summary: msg(
                'app.navigation.guest_safe_timeline_summary_for_visible_events',
              ),
            })
          : null,
        ...(shouldRenderSurface(profile, 'guest.shared_planning') &&
        includeTarget('shared_planning')
          ? [
              withStatus('shared_planning', {
                id: 'shared-planning',
                label: msg('general.modules.task_boards.name'),
                href: '/shared-planning',
                icon: 'users-three',
                summary: msg(
                  'app.navigation.review_board_tasks_claim_responsibilities_and_track_what_you_need_to_bring',
                ),
              }),
            ]
          : []),
        includeTarget('seating')
          ? withStatus('seating', {
              id: 'seating',
              label: msg('general.modules.seating.name'),
              href: '/seating',
              icon: 'table',
              summary: msg(
                'app.navigation.your_published_table_and_seat_assignment_where_available',
              ),
            })
          : null,
      ].filter(Boolean) as AppNavigationSection['items'],
    },
  ]
    .filter((section) => section.items.length > 0)
    .map(withAppNavigationSectionDefaults);
}
